import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { QuestionnairesGridApiService } from 'src/app/features/questionnaires/services/questionnairesGrid-api-service';
import { EApprovalStatus, StatusFlowDto } from 'src/app/models/approval.model';
import { QuestionnaireRow } from 'src/app/models/questionnaireGrid.model';
import { SvgIconComponent } from 'angular-svg-icon';
import { QuestionnairesGridStore } from 'src/app/features/questionnaires/store/questionnairesGrid-store';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GlobalStore } from 'src/app/store/global-store';

@Component({
    selector: 'que-questionnaire-status-flow',
    imports: [CommonModule, SvgIconComponent, TranslateDirective, TranslatePipe],
    templateUrl: './questionnaire-status-flow.component.html',
    styleUrls: ['./questionnaire-status-flow.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionnaireStatusFlowComponent implements OnChanges, OnInit, OnDestroy {

  @Input() guid?: string;
  @Input() questionnaire?: QuestionnaireRow | null;

  statusFlow: StatusFlowDto[] = [];

  #questionnairesGridApiService = inject(QuestionnairesGridApiService);
  #questionnairesGridStore = inject(QuestionnairesGridStore);
  #globalStore = inject(GlobalStore);
  #cdr = inject(ChangeDetectorRef);

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    const statusFlowSub = this.#globalStore.isOpenStatusFlow$.subscribe((isOpen) => {
      if (!isOpen) {
        this.close();
      }
    });

    this.subscriptions.add(statusFlowSub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['guid'] && this.guid) {
      this.loadStatusFlow(this.guid);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadStatusFlow(guid: string): void {
    this.#questionnairesGridApiService
      .getStatusFlow(guid)
      .subscribe({
        next: (response) => {
          this.statusFlow = response ?? [];
          this.#cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading status flow', err);
          this.statusFlow = [];
          this.#cdr.markForCheck();
        }
      });
  }

  getStatusLabelKey(item: StatusFlowDto, index: number): string {
    if (index === 0) {
      return 'column.approvals.statusFlow.requestSent';
    }

    switch (item.status) {
      case EApprovalStatus.Approved:
        return item.name
          ? 'column.approvals.statusFlow.approvedBy'
          : 'column.approvals.statusFlow.approved';

      case EApprovalStatus.Rejected:
        return item.name
          ? 'column.approvals.statusFlow.rejectedBy'
          : 'column.approvals.statusFlow.rejected';

      case EApprovalStatus.PendingApproval:
        return item.name
          ? 'column.approvals.statusFlow.pendingApprovalFor'
          : 'column.approvals.statusFlow.pendingApproval';

      default:
        return 'column.approvals.statusFlow.unknownStatus';
    }
  }

  getStatusDetailsKey(item: StatusFlowDto, index: number): string {
    if (index === 0) {
      return 'column.approvals.statusFlow.requestSent';
    }

    return item.isEnd
      ? 'column.approvals.statusFlow.secondManager'
      : 'column.approvals.statusFlow.firstManager';
  }

  trackByIndex(index: number): number {
    return index;
  }

  getStatusIcon(item: StatusFlowDto, index: number): string {
    if (index === 0) {
      return '/assets/images/send.svg';
    }

    switch (item.status) {
      case EApprovalStatus.Approved:
        return '/assets/images/grid/status/check_circle.svg';

      case EApprovalStatus.PendingApproval:
        return '/assets/images/grid/status/hourglass.svg';

      case EApprovalStatus.Rejected:
        return '/assets/images/grid/status/cancel.svg';

      default:
        return '/assets/images/grid/status/hourglass.svg';
    }
  }

  getIconClass(item: StatusFlowDto, index: number): string {
    if (index === 0) {
      return 'sent-icon';
    }

    switch (item.status) {
      case EApprovalStatus.Approved:
        return 'approved-icon';

      case EApprovalStatus.PendingApproval:
        return 'pending-icon';

      case EApprovalStatus.Rejected:
        return 'rejected-icon';

      default:
        return 'pending-icon';
    }
  }

  close(): void {
    this.#questionnairesGridStore.updateCurrentQuestionnaire({ questionnaire: null });
  }
}